import { z } from 'zod';

/**
 * Booking state machine — single source of truth.
 * Mirrors spec.md §10. The api FSM (apps/api/src/domain/booking/fsm.ts) imports these
 * and rejects invalid transitions with 409.
 */
export const BOOKING_STATES = [
  'draft',
  'reserved_pending_payment',
  'confirmed',
  'in_progress',
  'completed',
  'cancelled',
  'refunded',
  'overdue',
] as const;
export type BookingState = (typeof BOOKING_STATES)[number];

export const bookingStateSchema = z.enum(BOOKING_STATES);

/** key = from state, value = states that can be reached from it. */
export const BOOKING_TRANSITIONS: Record<BookingState, readonly BookingState[]> = {
  draft: ['reserved_pending_payment', 'cancelled'],
  reserved_pending_payment: ['confirmed', 'cancelled'],
  confirmed: ['in_progress', 'cancelled'],
  in_progress: ['completed', 'overdue', 'cancelled'],
  overdue: ['completed', 'cancelled'],
  completed: ['refunded'],
  cancelled: [],
  refunded: [],
};

export function canTransition(from: BookingState, to: BookingState): boolean {
  return BOOKING_TRANSITIONS[from].includes(to);
}

/** Payment kinds + methods + states (mirrored from Drizzle enums for type-safe shared validators). */
export const PAYMENT_KINDS = ['deposit', 'balance', 'refund', 'caucao'] as const;
export type PaymentKind = (typeof PAYMENT_KINDS)[number];

export const PAYMENT_METHODS = ['vinti4', 'transfer', 'cash', 'multibanco', 'stripe'] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];

export const PAYMENT_STATES = ['pending', 'succeeded', 'failed', 'refunded'] as const;
export type PaymentState = (typeof PAYMENT_STATES)[number];

export const REVIEW_STATES = ['pending', 'approved', 'rejected'] as const;
export type ReviewState = (typeof REVIEW_STATES)[number];

export const BODY_TYPES = ['suv', 'sedan', 'pickup'] as const;
export type BodyType = (typeof BODY_TYPES)[number];

export const ROLES = ['admin', 'customer'] as const;
export type Role = (typeof ROLES)[number];

/** Booking pricing breakdown — returned by GET /v1/me/bookings/:id and used by the booking summary card. */
export const bookingPricingSchema = z.object({
  days: z.number().int().positive(),
  pricePerDayEur: z.number().nonnegative(),
  pricePerDayCve: z.number().nonnegative(),
  subtotalEur: z.number().nonnegative(),
  subtotalCve: z.number().nonnegative(),
  addonsEur: z.number().nonnegative(),
  addonsCve: z.number().nonnegative(),
  locationFeeEur: z.number().nonnegative(),
  locationFeeCve: z.number().nonnegative(),
  /** Young-driver surcharge (feature 002 D2, RD-304) — 0 when no rule applies. */
  youngDriverSurchargeEur: z.number().nonnegative().default(0),
  youngDriverSurchargeCve: z.number().nonnegative().default(0),
  weeklyDiscountPct: z.number().min(0).max(100),
  weeklyDiscountEur: z.number().nonnegative(),
  weeklyDiscountCve: z.number().nonnegative(),
  totalEur: z.number().nonnegative(),
  totalCve: z.number().nonnegative(),
  /** Deposit % in force when the booking was priced (admin setting `deposit_pct`, 005). */
  depositPctApplied: z.number().int().min(1).max(100),
  depositEur: z.number().nonnegative(),
  depositCve: z.number().nonnegative(),
  balanceEur: z.number().nonnegative(),
  balanceCve: z.number().nonnegative(),
  caucaoEur: z.number().nonnegative(),
  caucaoCve: z.number().nonnegative(),
});
export type BookingPricing = z.infer<typeof bookingPricingSchema>;

/**
 * Admin-tunable young-driver rule (feature 002 D2, RD-304) — stored in
 * `settings` under key `young_driver_rule`. All values are entered by the admin
 * (no hardcoded threshold/% — §4). When the key is absent, no surcharge applies
 * and there is no age floor.
 */
export const YOUNG_DRIVER_RULE_KEY = 'young_driver_rule';
export const youngDriverRuleSchema = z.object({
  /** Drivers strictly younger than this pay the surcharge. */
  thresholdAge: z.number().int().min(16).max(99),
  /** Per-day surcharge in each currency (admin-entered). */
  surchargePerDayEur: z.number().nonnegative(),
  surchargePerDayCve: z.number().nonnegative(),
  /** Optional hard floor — drivers younger than this are ineligible (booking rejected). */
  hardMinAge: z.number().int().min(16).max(99).optional(),
});
export type YoungDriverRule = z.infer<typeof youngDriverRuleSchema>;

/** Booking code generator. Format: LVM-YYYY-NNNN (1-based, year-scoped). */
export function bookingCode(year: number, seq: number): string {
  return `LVM-${year}-${String(seq).padStart(4, '0')}`;
}

/** Same regex used by both web and admin to validate manual entries. */
export const BOOKING_CODE_REGEX = /^LVM-\d{4}-\d{4}$/;

/**
 * Share of the total charged online to reserve (client request 005) — stored in `settings`
 * under `deposit_pct` as `{ value: n }`. Each booking freezes the % it was priced with in its
 * pricing snapshot, so changing the setting never alters an existing booking.
 */
export const DEPOSIT_PCT_KEY = 'deposit_pct';
export const DEFAULT_DEPOSIT_PCT = 20;
export const depositPctSettingSchema = z.object({ value: z.number().int().min(1).max(100) });
